import { initContract } from '@ts-rest/core'
import z from 'zod'
import { ErrorResponseSchema } from '../../shared/types'
import {
  ListOrderRefundsResponseSchema,
  OrderRefundSchema,
  RecordOrderRefundBodySchema,
} from './schemas'

const c = initContract()

const options = {
  pathPrefix: '/api/v1',
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    409: ErrorResponseSchema,
    500: ErrorResponseSchema,
  },
}

/**
 * Order-side refund records (`order_refund`). Orchestration records one row
 * per completed payment refund; a subscriber will turn that row into the
 * NetSuite refund outbox row locally. Kept as its own router (the payment
 * service pattern) rather than growing `orderServiceContract`.
 */
export const orderRefundsContract = c.router(
  {
    recordOrderRefund: {
      method: 'POST',
      description: 'Record a completed payment refund against an order: one row per refund, full (creditedLines null) and partial alike. The row stores refundId + creditedLines only — the money stays in payment.refunds, fetched by refundId. refundAmountCents is the money the refund moved: a partial whose creditedLines do not sum to it is rejected with a 400 and nothing is recorded. Idempotent per refund: re-recording the same refundId for the same order returns the existing row as 200 (created rows return 201). Returns 404 if the order does not exist, 409 if the refundId is already recorded against a different order.',
      path: '/orders/:orderId/refunds',
      pathParams: z.object({ orderId: z.string().uuid() }),
      body: RecordOrderRefundBodySchema,
      responses: {
        200: OrderRefundSchema,
        201: OrderRefundSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
      },
    },

    listOrderRefunds: {
      method: 'GET',
      description: 'Return all refund records for an order in insertion order. Used to derive refund state (e.g. sum of completed refunds vs captured) and the optional REFUNDED display label. Returns an empty list for an order with no refunds.',
      path: '/orders/:orderId/refunds',
      pathParams: z.object({ orderId: z.string().uuid() }),
      responses: {
        200: ListOrderRefundsResponseSchema,
      },
    },
  },
  options,
)
